// ProductPreview.js

import React from 'react';
import { useNavigate } from 'react-router-dom';

import TitleField from '../text/TitleField';
import TextField from '../text/TextField';

import '../../styles/designs/ProductPreview.css';

/**
 * ProductPreview
 * A clickable product card used in the Home page's Products Overview.
 *
 * The device image is sized entirely from CSS variables in
 * `ProductPreview.css`, keyed off two modifier classes:
 *   - orientation → `--vertical` | `--horizontal`  (card layout)
 *   - deviceType  → `--phone`    | `--tablet`       (device width token)
 *
 * Because the device width comes from a shared token, a phone renders at the
 * SAME width in a vertical or a horizontal card — so a horizontal card's
 * device stacks symmetrically beneath a vertical card's device.
 *
 * Props:
 *  - imgSource:   image src for the product device/frame
 *  - title:       product title
 *  - description: short product description
 *  - orientation: "vertical" (image on top, text below) or
 *                 "horizontal" (text beside image)
 *  - deviceType:  "phone" or "tablet" (selects the width token)
 *  - to:          route to navigate to on click (e.g. "/products#nichewallet")
 */
const ProductPreview = ({
    imgSource,
    title,
    description,
    orientation = 'vertical',
    deviceType = 'phone',
    to,
}) => {
    const navigate = useNavigate();

    const variant = orientation === 'horizontal' ? 'horizontal' : 'vertical';
    const device = deviceType === 'tablet' ? 'tablet' : 'phone';

    const handleClick = () => {
        if (to) navigate(to);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
        }
    };

    return (
        <div
            className={`product-preview-container product-preview-container--${variant} product-preview-container--${device}`}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <div
                className={`product-preview-image-container product-preview-image-container--${variant} product-preview-image-container--${device}`}
            >
                <img
                    className={`product-preview-image product-preview-image--${device}`}
                    src={imgSource}
                    alt={title}
                    draggable={false}
                />
            </div>

            <div className={`product-preview-text-container product-preview-text-container--${variant}`}>
                <div className="product-preview-title-container">
                    <TitleField title={title} bold={true} fontSize="1.6rem" />
                </div>

                <div className="product-preview-description-container">
                    <TextField text={description} fontColor="#6b7280" />
                </div>
            </div>
        </div>
    );
};

export default ProductPreview;
